import React, { useEffect, useRef, useState } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { alpha } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { FulfilmentStatus } from '../../../core/state/order/order.types';
import { Button, Typography } from '../../atoms';
import { ImageDimensionsType } from '../../atoms/Image/Image';
import { SteppedProgressBar } from '../../molecules';
import { OrderStatusSkeleton } from './components/OrderStatusSkeleton';
import { SlidingImage } from './components/SlidingImage';

export interface OrderStatusProps {
  className?: string;
  dataFd?: string;
  orderStatus: FulfilmentStatus;
  translate: TranslateFunction;
  variant?: 'header' | 'subHeader';
}

type SlidingImageType = { url: string; isEnterAnimation: boolean };

const useStyles = makeStyles((theme: Theme) => ({
  driverTrackingLink: {
    padding: theme.spacing(0),
  },

  errorStatus: {
    '& .fd-text': {
      color: theme.palette.error.main,
    },
  },

  etaDescription: {
    paddingBottom: theme.spacing(1),
    textAlign: 'right',
  },

  headerDescription: {
    paddingTop: theme.spacing(0.5),
  },

  headerDescriptionError: {
    paddingBottom: theme.spacing(2.5),
  },

  headerTop: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },

  image: {
    minHeight: '144px',
    minWidth: '344px',
    paddingLeft: theme.spacing(2),
  },

  imageContainer: {
    position: 'relative',
    backgroundColor: alpha(theme.palette.info.main, 0.25),
    borderRadius: '72px 0px 0px 72px',
    height: '144px',
    margin: theme.spacing(1, 0, 6, 2),
    overflow: 'hidden',
  },

  imageContainerError: {
    backgroundColor: alpha(theme.palette.error.main, 0.25),
  },

  orderStatus: {
    padding: theme.spacing(0, 2),
  },

  subHeaderDescription: {
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(1.5),
  },

  subHeaderDescriptionError: {
    paddingTop: theme.spacing(1.5),
  },

  [theme.breakpoints.up('sm')]: {
    orderStatus: {
      padding: theme.spacing(0, 3),
    },
  },

  [theme.breakpoints.up('md')]: {
    orderStatus: {
      padding: theme.spacing(0),
    },
  },
}));

export function OrderStatus(props: OrderStatusProps): JSX.Element {
  const classes = useStyles();
  const {
    className = '',
    dataFd = 'order-status',
    orderStatus,
    translate,
    variant = 'header',
  } = props;

  const [slidingImages, setSlidingImages] = useState<SlidingImageType[]>([]);
  const isSlideEnabled = useRef(false);

  useEffect(() => {
    if (slidingImages.length === 0) {
      setSlidingImages([
        { url: orderStatus?.ImageUrl, isEnterAnimation: true },
      ]);
    } else {
      const newSlidingImages = [...slidingImages];
      newSlidingImages[0].isEnterAnimation = false;
      newSlidingImages.push({
        url: orderStatus?.ImageUrl,
        isEnterAnimation: true,
      });
      setSlidingImages(newSlidingImages);
      isSlideEnabled.current = true;
    }
  }, [orderStatus?.ImageUrl]);

  if (variant === 'subHeader' && !orderStatus?.StatusName) {
    return null;
  }

  if (!orderStatus?.StatusName) {
    return <OrderStatusSkeleton />;
  }

  const {
    DeliveryTrackingUrl: deliveryTrackingUrl,
    Description: description,
    EstimatedCompletionDescription: estimatedCompletionDescription,
    ImageUrl: imageUrl,
    IsError: isError,
    StatusName: statusName,
    StatusPosition: statusPosition,
    TotalStatuses: totalStatuses,
  } = orderStatus;

  const removeImageFromArray = (url: string): void => {
    let newSlidingImages = [...slidingImages];
    newSlidingImages = newSlidingImages.filter((x) => x.url !== url);
    setSlidingImages(newSlidingImages);
  };

  const isHeader = variant === 'header';
  const showProgressBar = !isError && totalStatuses > 0;

  const orderStatusStyles = clsx(
    'fd-order-status',
    classes.orderStatus,
    isError && classes.errorStatus,
    className !== '' && className,
  );

  const renderProgressBar = (): JSX.Element => {
    const progressBarVariant = isHeader ? 'default' : 'slim';

    return (
      <SteppedProgressBar
        currentStep={statusPosition}
        dataFd="order-status-progress-bar"
        totalSteps={totalStatuses}
        variant={progressBarVariant}
      />
    );
  };

  const renderHeader = (): JSX.Element => {
    const showEtaDescription = !isError && estimatedCompletionDescription;
    const imageDimensions: ImageDimensionsType = [344, 144];

    const descriptionStyles = clsx(
      classes.headerDescription,
      isError && classes.headerDescriptionError,
    );

    const imageContainerStyles = clsx(
      'fd-order-status-image',
      classes.imageContainer,
      isError && classes.imageContainerError,
    );

    return (
      <>
        <div className={orderStatusStyles}>
          <div className={classes.headerTop}>
            <Typography variant="h1">{statusName}</Typography>

            {deliveryTrackingUrl && (
              <Button
                className={classes.driverTrackingLink}
                href={deliveryTrackingUrl}
                rel="noopener noreferrer"
                size="small"
                target="_blank"
                variant="tertiary"
              >
                {translate('OrderDetails_trackDriver')}
              </Button>
            )}
          </div>

          <Typography className={descriptionStyles} variant="body2-secondary">
            {description}
          </Typography>

          {showProgressBar && renderProgressBar()}

          {showEtaDescription && (
            <Typography className={classes.etaDescription} variant="caption">
              {estimatedCompletionDescription}
            </Typography>
          )}
        </div>

        {imageUrl && (
          <div className={imageContainerStyles}>
            {slidingImages.map((slidingImage) => (
              <SlidingImage
                className={classes.image}
                imageDimensions={imageDimensions}
                imageUrl={slidingImage.url}
                isEnterAnimation={slidingImage.isEnterAnimation}
                isSlideAnimationEnabled={isSlideEnabled.current}
                key={slidingImage.url}
                onExitAnimationEnd={(): void =>
                  removeImageFromArray(slidingImage.url)
                }
                statusName={statusName}
              />
            ))}
          </div>
        )}
      </>
    );
  };

  const renderSubHeader = (): JSX.Element => {
    const descriptionText =
      !isError && estimatedCompletionDescription
        ? `${statusName}, ${estimatedCompletionDescription}`
        : `${statusName}, ${description}`;

    const descriptionStyles = clsx(
      classes.subHeaderDescription,
      isError && classes.subHeaderDescriptionError,
    );

    return (
      <div className={orderStatusStyles}>
        {showProgressBar && renderProgressBar()}

        <Typography className={descriptionStyles} variant="body2-secondary">
          {descriptionText}
        </Typography>
      </div>
    );
  };

  return (
    <div data-fd={dataFd}>{isHeader ? renderHeader() : renderSubHeader()}</div>
  );
}
